import { ClubType, UserProfile, GolfClub } from "../types";

// Distance estimation based on swing speed and club type
export const estimateClubDistances = (userProfile: UserProfile): { [key in ClubType]?: number } => {
  let swingSpeed = userProfile.swingSpeed;
  
  // If no exact swing speed, estimate from range
  if (!swingSpeed && userProfile.swingSpeedRange) {
    const rangeMap = {
      "under-85": 80,
      "85-95": 90,
      "95-105": 100,
      "over-105": 110
    };
    swingSpeed = rangeMap[userProfile.swingSpeedRange];
  }
  
  // Default to 95 mph if no data
  if (!swingSpeed) swingSpeed = 95;
  
  // Base distances for 95 mph swing speed (carry distances)
  const baseDistances: { [key in ClubType]: number } = {
    "driver": 250,
    "3-wood": 225,
    "5-wood": 210,
    "7-wood": 195,
    "2-hybrid": 200,
    "3-hybrid": 190,
    "4-hybrid": 180,
    "5-hybrid": 170,
    "3-iron": 185,
    "4-iron": 175,
    "5-iron": 165,
    "6-iron": 155,
    "7-iron": 145,
    "8-iron": 135,
    "9-iron": 125,
    "pw": 115,
    "aw": 105,
    "gw": 100,
    "sw": 85,
    "lw": 70,
    "putter": 0
  };
  
  // Calculate scaling factor based on swing speed
  const scaleFactor = swingSpeed / 95;
  
  // Apply scaling and round to nearest 5 yards
  const estimatedDistances: { [key in ClubType]?: number } = {};
  
  Object.entries(baseDistances).forEach(([club, distance]) => {
    const scaledDistance = distance * scaleFactor;
    estimatedDistances[club as ClubType] = Math.round(scaledDistance / 5) * 5;
  });
  
  return estimatedDistances;
};

// Calculate gapping analysis
export interface GapAnalysis {
  overlaps: { club1: ClubType; club2: ClubType; gap: number }[];
  gaps: { beforeClub: ClubType; afterClub: ClubType; gap: number }[];
  gappingScore: number; // 0-100, higher is better
  recommendations: string[];
}

export const analyzeGapping = (clubs: GolfClub[]): GapAnalysis => {
  // Filter clubs with distances, exclude putters (no carry distance), sort by distance (descending)
  const clubsWithDistances = clubs
    .filter(club => club.distance && club.distance > 0 && club.type !== 'putter')
    .sort((a, b) => (b.distance || 0) - (a.distance || 0));
  
  const overlaps: { club1: ClubType; club2: ClubType; gap: number }[] = [];
  const gaps: { beforeClub: ClubType; afterClub: ClubType; gap: number }[] = [];
  const recommendations: string[] = [];
  
  // Analyze gaps between consecutive clubs
  for (let i = 0; i < clubsWithDistances.length - 1; i++) {
    const longerClub = clubsWithDistances[i];
    const shorterClub = clubsWithDistances[i + 1];
    const gap = (longerClub.distance || 0) - (shorterClub.distance || 0);
    
    // Ideal gaps by club type
    const getIdealGap = (club: ClubType): number => {
      if (club === "driver") return 20; // Driver to next club
      if (club.includes("wood") || club.includes("hybrid")) return 15;
      if (club.includes("iron")) return 12;
      return 10; // wedges
    };
    
    const idealGap = getIdealGap(longerClub.type);
    
    if (gap < 5) {
      // Overlap - clubs too close
      overlaps.push({
        club1: longerClub.type,
        club2: shorterClub.type,
        gap: gap
      });
      recommendations.push(`${longerClub.type} and ${shorterClub.type} have similar distances (${gap} yard gap). Consider removing one.`);
    } else if (gap > idealGap + 8) {
      // Gap too large
      gaps.push({
        beforeClub: longerClub.type,
        afterClub: shorterClub.type,
        gap: gap
      });
      recommendations.push(`Large ${gap}-yard gap between ${longerClub.type} and ${shorterClub.type}. Consider adding a club that goes ${Math.round((longerClub.distance! + shorterClub.distance!) / 2)} yards.`);
    }
  }
  
  // Calculate gapping score (0-100)
  const totalProblems = overlaps.length + gaps.length;
  const maxProblems = Math.max(clubsWithDistances.length / 2, 5); // Reasonable max
  const gappingScore = Math.max(0, Math.round(100 - (totalProblems / maxProblems) * 100));
  
  // Add general recommendations
  if (clubsWithDistances.length < 10) {
    recommendations.push("Consider adding more clubs to fill distance gaps and give yourself more options on the course.");
  }
  
  if (overlaps.length === 0 && gaps.length === 0) {
    recommendations.push("Great gapping! Your clubs are well-spaced for consistent distance control.");
  }
  
  return {
    overlaps,
    gaps,
    gappingScore,
    recommendations
  };
};

// Estimate distance from swing speed for specific club
export const estimateDistanceForClub = (clubType: ClubType, swingSpeed: number): number => {
  const profile = { swingSpeed } as UserProfile;
  const distances = estimateClubDistances(profile);
  return distances[clubType] || 0;
};

// Calculate swing speed from distance (reverse engineering)
export const estimateSwingSpeedFromDistance = (clubType: ClubType, distance: number): number => {
  const baseDistances: { [key in ClubType]: number } = {
    "driver": 250,
    "3-wood": 225,
    "5-wood": 210,
    "7-wood": 195,
    "2-hybrid": 200,
    "3-hybrid": 190,
    "4-hybrid": 180,
    "5-hybrid": 170,
    "3-iron": 185,
    "4-iron": 175,
    "5-iron": 165,
    "6-iron": 155,
    "7-iron": 145,
    "8-iron": 135,
    "9-iron": 125,
    "pw": 115,
    "aw": 105,
    "gw": 100,
    "sw": 85,
    "lw": 70,
    "putter": 0
  };
  
  const baseDistance = baseDistances[clubType];
  if (!baseDistance) return 95; // Default
  
  const scaleFactor = distance / baseDistance;
  return Math.round(scaleFactor * 95);
};
